import React, { useCallback, useState } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SpecialTag } from '../../SpecialTag';
import { WorkedInRoutePills } from '../WorkedInRoutePills';
import { UnlinkedRoutePills } from '../UnlinkedRoutePills';

// Types
import { ShowOTAccidentProps } from './types';
import { variant } from '../../SpecialTag/types';
import { OrderState, OrderType } from '../../../../types/orders';
import { Role } from '../../../../types/auth';
import { useAuth } from '../../../../hooks/useAuth';
import { modalIds } from '../../../modals/SuperModal/types';

// Styles Components
import {
  ContainerContent,
  ContainerData,
  ContainerDescription,
  ContainerHeader,
  ContainerRefIcon,
  ContainerTitleArrow,
  InputAction,
  InputDescription,
  RefRoid,
  RightArrow,
  TagsContainer,
  TitleDate,
  TitleOrder,
} from '../MainStyledComponents';
import { ContainerOTStyled, ButtonViewDetail } from './styles';

// Components
import { DateTime } from '../../../inputs/DateTime';

// Icons
import ArrowRight from '../../../../assets/icons/triangle-right.svg';

// Utils
import { useIsLandscapeTablet } from '../../../../hooks/useMediaBreakPoints';
import { dataTransferNaming, formActionNaming } from '../../../forms/ActionForms/constants';
import { compressSessionStorage } from '../../../../utils/sessionStorage';
import { rememberChildCardAnchor } from '../../../../utils/childCardAnchor';
import { formAccidentNaming } from '../../../forms/AddBasicInfoAccidentForm/constants';
import { formVehicleNaming } from '../../../forms/AddVehicleForm/constants';
import { formNaming as formCommunicationNaming } from '../../../forms/NewCommunicationForm/constants';

// Redux
import { useDispatch } from 'react-redux';
import { openModal } from '../../../../state/actions/ui/modal';
import { roidOTSelected } from '../../../../state/actions/ui/roidOTSelectedGeneralView';

export const AccidentOT = ({
  roid,
  tag,
  date,
  capitol,
  capitols,
  subCapitol,
  subCapitolsValues,
  predefine,
  predefinedOrders,
  description,
  notes,
  pkInit,
  pkEnd,
  isTransferred = false,
  id,
  accidentID,
  parentOrder,
  revalidatorFunction,
  state,
  stateParentExpeditionOrder,
  userName,
  registeredBy,
  imageIDs,
  workedInRoutes,
  unlinkedRoutes,
  initiallyExpanded,
}: ShowOTAccidentProps): JSX.Element => {
  const { t } = useTranslation(['common', 'forms']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentRole = user?.user?.rolActive as Role | undefined;
  const [showInfo, setShowInfo] = useState(initiallyExpanded ?? false);
  const isLandscapeTablet = useIsLandscapeTablet();
  const toggleInfo = useCallback(() => setShowInfo(!showInfo), [showInfo]);

  // Handler para navegar al detalle del accidente
  const handleViewDetail = useCallback(() => {
    // BUG FIX: Limpiar contextos de otras entidades antes de setear el de accidente
    sessionStorage.removeItem(formCommunicationNaming.COMMUNICATION_NAVIGATION_CONTEXT);
    sessionStorage.removeItem(formActionNaming.NEW_VALIDATION_ACTION_DATA);

    // Ancla de vuelta: al volver, `ShowOT` traerá esta tarjeta a la vista y la
    // dejará desplegada. Cubre las dos puertas — el botón «Volver» (que hace
    // PUSH) y la flecha atrás del navegador (POP).
    rememberChildCardAnchor(parentOrder?.id, roid);

    // Store navigation context before navigating
    sessionStorage.setItem(
      formAccidentNaming.ACCIDENT_NAVIGATION_CONTEXT,
      compressSessionStorage(
        JSON.stringify({
          origin: 'workorder',
          parentOrderId: parentOrder?.id,
          parentRoid: parentOrder?.roid, // Parent order ROID for breadcrumb
          parentOrderType: parentOrder?.type, // Parent order type for campaign detection
          entityRoid: roid, // Accident ROID for breadcrumb
        })
      )
    );

    // Dispatch roidOTSelected to update Redux state for breadcrumb switch case
    dispatch(roidOTSelected(OrderType.Accident, roid, accidentID));

    // Context-aware navigation: detect if parent is a campaign
    const isCampaign = parentOrder?.type === OrderType.Campaign;

    if (isCampaign && parentOrder?.id) {
      // Navigate within campaign context
      navigate(`/work-orders/campaign/${parentOrder.id}/accidents/detail/${accidentID}`);
    } else {
      // Navigate to global accident route
      navigate(`/work-orders/accidents/detail/${accidentID}`);
    }
  }, [accidentID, dispatch, navigate, parentOrder?.id, parentOrder?.type, roid]);

  // Handler para editar el accidente
  const handleEditAccident = useCallback(() => {
    // BUG FIX: Limpiar contextos de otras entidades antes de setear el de accidente
    sessionStorage.removeItem(formCommunicationNaming.COMMUNICATION_NAVIGATION_CONTEXT);
    sessionStorage.removeItem(formActionNaming.NEW_VALIDATION_ACTION_DATA);

    // Clear edit form storage
    sessionStorage.removeItem(formAccidentNaming.EDIT_ACCIDENT_FORM);
    sessionStorage.removeItem(formAccidentNaming.EDIT_BASIC_INFO_ACCIDENT_FORM);
    sessionStorage.removeItem(formVehicleNaming.EDIT_VEHICLE_FORM);

    // Store navigation context before navigating
    sessionStorage.setItem(
      formAccidentNaming.ACCIDENT_NAVIGATION_CONTEXT,
      compressSessionStorage(
        JSON.stringify({
          // De qué accidente es (la Order, el `:id` de la URL de editar): la
          // edición lo comprueba al llegar y descarta el contexto de otro
          // (R5, spec wizard-draft-ownership § 6).
          entityId: id,
          origin: 'workorder',
          parentOrderId: parentOrder?.id,
          parentRoid: parentOrder?.roid, // Parent order ROID for breadcrumb
          parentOrderType: parentOrder?.type, // Parent order type for campaign detection
          entityRoid: roid, // Accident ROID for breadcrumb
        })
      )
    );

    // Dispatch roidOTSelected to update Redux state for breadcrumb switch case
    // Use accidentID for breadcrumb tracking, but id (Order ID) for navigation
    dispatch(roidOTSelected('edit-accident', roid, accidentID));

    // BUG FIX: Use `id` (Order ID) not `accidentID` (Accident entity ID)
    // The EditAccidentWorkOrderLoader expects Order ID to fetch data correctly

    // Context-aware navigation: detect if parent is a campaign
    const isCampaign = parentOrder?.type === OrderType.Campaign;

    if (isCampaign && parentOrder?.id) {
      // Navigate within campaign context
      navigate(`/work-orders/campaign/${parentOrder.id}/accidents/edit-accident/${id}`);
    } else {
      // Navigate to global accident route
      navigate(`/work-orders/accidents/edit-accident/${id}`);
    }
  }, [accidentID, dispatch, id, navigate, parentOrder?.id, parentOrder?.type, roid]);

  const openModalTransferSubOrder = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    dispatch(
      openModal(modalIds.TRANSFER_SUBORDER_MODAL, {
        namingPersistForm: dataTransferNaming.SUB_ORDER_ACCIDENT,
        orderToTransfer: {
          type: OrderType.Accident,
          id,
          orderParent: parentOrder.id,
        },
        currentParentOrder: {
          id: parentOrder.id,
          type: parentOrder.type,
        },
        revalidatorFunction: revalidatorFunction,
      })
    );
  };
  return (
    <ContainerOTStyled data-child-roid={roid}>
      <ContainerHeader onClick={toggleInfo}>
        <TitleOrder>
          {t('orderTitles.accident')}
          <TitleDate>{` ${format(
            new Date(date),
            'dd/MM/yyyy - HH:mm'
          )}`}</TitleDate>
        </TitleOrder>
        <ContainerTitleArrow>
          <ContainerRefIcon>
            <RefRoid>{roid}</RefRoid>
            <TagsContainer>
              <SpecialTag
                variant={tag}
                sizeTag={isLandscapeTablet ? 'small' : 'default'}
              />
              {(isTransferred ||
                (state !== OrderState.Annulled &&
                  stateParentExpeditionOrder !== OrderState.Annulled &&
                  stateParentExpeditionOrder !== OrderState.End)) && (
                <SpecialTag
                  variant={isTransferred ? variant.Transferred : variant.Transfer}
                  sizeTag={isLandscapeTablet ? 'small' : 'default'}
                  onClick={
                    !isTransferred
                      ? (e) => openModalTransferSubOrder(e)
                      : undefined
                  }
                />
              )}
              {/* Spec 2026-09-23: el recorrido cuya patrulla hizo esta hija. */}
              <WorkedInRoutePills routes={workedInRoutes} />
              {/* Spec § 16: una patrulla SIN vincular la hizo → aviso. */}
              <UnlinkedRoutePills
                routes={unlinkedRoutes}
                parentOrderId={parentOrder.id}
                revalidatorFunction={revalidatorFunction}
              />
            </TagsContainer>
          </ContainerRefIcon>
          <RightArrow
            src={ArrowRight}
            alt={t('common:expandInfo')}
            showInfo={showInfo}
          />
        </ContainerTitleArrow>
      </ContainerHeader>
      {showInfo ? (
        <ContainerContent>
          <ContainerData>
            <DateTime
              label={t('forms:workOrder.registerDate')}
              labelHour={t('forms:workOrder.hour')}
              isDisabled={true}
              initialDate={date}
            />

            {userName && (
              <InputAction
                label={t('forms:workOrder.createdBy')}
                typeInput="normal"
                disabled={true}
                value={userName}
              />
            )}

            {registeredBy && (
              <InputAction
                label={t('forms:registeredBy.label')}
                typeInput="normal"
                disabled={true}
                value={registeredBy}
              />
            )}

            {pkInit && (
              <InputAction
                label={t('forms:workOrder.pkInitial')}
                typeInput="normal"
                disabled={true}
                value={pkInit}
              />
            )}
            {pkEnd && (
              <InputAction
                label={t('forms:workOrder.pkFinal')}
                typeInput="normal"
                disabled={true}
                value={pkEnd}
              />
            )}

            <InputAction
              label={t('forms:workOrder.predefined')}
              typeInput="normal"
              disabled={true}
              value={
                predefinedOrders?.find(
                  (predefinedOrder) => predefinedOrder.id === predefine
                )?.name
              }
            />

            <InputAction
              label={t('forms:workOrder.chapter')}
              typeInput="normal"
              disabled={true}
              value={
                capitols?.find((capitolInter) => capitolInter.id === capitol)
                  ?.name
              }
            />

            <InputAction
              label={t('forms:workOrder.subchapter')}
              typeInput="normal"
              disabled={true}
              value={
                subCapitolsValues?.find(
                  (subCapitolInter) => subCapitolInter.id === subCapitol
                )?.name
              }
            />
          </ContainerData>
          <ContainerDescription>
            <InputDescription
              label={t('forms:workOrder.description')}
              disabled={true}
              value={description}
            />
            {notes && (
              <InputDescription
                label={t('forms:workOrder.notes')}
                disabled={true}
                value={notes}
              />
            )}
            {imageIDs && imageIDs.length > 0 && (
              <ButtonViewDetail
                variant="download"
                onClick={() => {
                  dispatch(
                    openModal(modalIds.DOWNLOAD_MULTIMEDIA_PHOTOS, {
                      selectedRows: [id],
                    })
                  );
                }}
                type="button"
              />
            )}
            <ButtonViewDetail
              variant="edit"
              onClick={handleEditAccident}
              type="button"
              disabled={
                state === OrderState.Annulled ||
                stateParentExpeditionOrder === OrderState.Annulled ||
                stateParentExpeditionOrder === OrderState.End ||
                currentRole === Role.RoomTeam ||
                (currentRole === Role.Tablet && stateParentExpeditionOrder !== OrderState.WorkInProgress)
              }
            />
            <ButtonViewDetail
              variant="visibility"
              onClick={handleViewDetail}
              type="button"
            />
          </ContainerDescription>
        </ContainerContent>
      ) : null}
    </ContainerOTStyled>
  );
};
